// Язык интерфейса: en, ru, es, pt, de, fr, it (иное → en, как на сервере). Строки — из каталога MLS iOS
// (scripts/import_ios_strings.mjs → strings/<lang>.json) плюс свои строки Mini App (extra/*.json: по файлу на область —
// shell, matchCenter, match, menu, экраны W2–W3…, в каждом все семь языков; строки экранов W2–W3 — ленивые, с экраном:
// extraFiles.ts, lazyScreen.ts). Подписи вкладок, статусы, empty_state приходят с сервера
// переведёнными. Совпадение ключа extra с каталогом iOS или между файлами extra — ошибка (tests/shell/i18n.test.ts).
import en from './strings/en.json';
import { telegramUser } from '../telegram/webapp';
import { areaStrings, type ExtraFile, firstScreenStrings, type StringArea, type Table } from './extraFiles';

export const LANGUAGES = ['en', 'ru', 'es', 'pt', 'de', 'fr', 'it'] as const;
export type Lang = (typeof LANGUAGES)[number];

type ExtraKeys<T> = T extends { en: infer E } ? keyof E : never;
/** Ключи строк: каталог iOS и все файлы extra (новый файл extra — добавить сюда строкой). */
export type StringKey = keyof typeof en
  | ExtraKeys<typeof import('./extra/shell.json')>
  | ExtraKeys<typeof import('./extra/matchCenter.json')>
  | ExtraKeys<typeof import('./extra/match.json')>
  | ExtraKeys<typeof import('./extra/menu.json')>
  | ExtraKeys<typeof import('./extra/leagues.json')>
  | ExtraKeys<typeof import('./extra/tournament.json')>
  | ExtraKeys<typeof import('./extra/worldCup.json')>
  | ExtraKeys<typeof import('./extra/team.json')>
  | ExtraKeys<typeof import('./extra/player.json')>
  | ExtraKeys<typeof import('./extra/search.json')>
  | ExtraKeys<typeof import('./extra/news.json')>
  | ExtraKeys<typeof import('./extra/highlights.json')>
  | ExtraKeys<typeof import('./extra/favorites.json')>
  | ExtraKeys<typeof import('./extra/notifications.json')>;

/** Язык из кода (`pt-BR` → pt, `DE` → de); не из семи — null. */
export function supportedLang(code: string | undefined | null): Lang | null {
  const base = (code ?? '').toLowerCase().split(/[-_]/)[0] ?? '';
  return (LANGUAGES as readonly string[]).includes(base) ? (base as Lang) : null;
}

export function normalizeLang(code: string | undefined | null): Lang {
  return supportedLang(code) ?? 'en';
}

/** Язык интерфейса по источникам в порядке старшинства: первый из семи, иначе en. Незнакомое значение (испорченное
 *  хранилище, язык Telegram не из семи) пропускается, а не сбрасывает в en. */
export function resolveLang(...candidates: Array<string | undefined | null>): Lang {
  for (const code of candidates) {
    const found = supportedLang(code);
    if (found) return found;
  }
  return 'en';
}

const LANG_KEY = 'football.lang';
function storedLang(): string | null { try { return localStorage.getItem(LANG_KEY); } catch { return null; } }

/** Язык: ?lang= (отладка) → выбранный человеком в меню → language_code из Telegram → en. Первый запуск — язык Telegram;
 *  выбор в меню главнее языка Telegram и переживает закрытие. */
export const lang: Lang = resolveLang(new URLSearchParams(location.search).get('lang'), storedLang(), telegramUser()?.language_code);

/** Названия языков — на самих языках, не переводятся. */
export const LANGUAGE_NAMES: Record<Lang, string> = {
  en: 'English', ru: 'Русский', es: 'Español', pt: 'Português', de: 'Deutsch', fr: 'Français', it: 'Italiano',
};

/** Языки меню: config.supported_languages в порядке сервера — только те, что клиент умеет (словарь есть), без
 *  повторов; список пуст или его нет — все семь своего словаря. */
export function languageOptions(supported: readonly string[] | undefined): Lang[] {
  const list = [...new Set((supported ?? []).map(supportedLang).filter((l): l is Lang => l !== null))];
  return list.length ? list : [...LANGUAGES];
}

/** Сменить язык интерфейса: сохраняем и перезапускаем приложение (словарь и ?lang= всех запросов — на старте). */
export function setLanguage(next: Lang): void {
  try { localStorage.setItem(LANG_KEY, next); } catch { /* приватный режим — до перезапуска */ }
  location.reload();
}

/** Подключённые файлы extra: область → файл. На старте — оболочка и первый экран, остальные — loadArea. */
const extras: Record<string, ExtraFile> = { ...firstScreenStrings };

/** Свои строки языка из файлов extra (по умолчанию — из уже подключённых). */
export function extraStrings(language: Lang, files: Record<string, ExtraFile> = extras): Table {
  return Object.assign({}, ...Object.values(files).map((file) => file[language] ?? {})) as Table;
}

let ios: Table = {};
let fallback: Table = {};
let table: Table = {};
function rebuild() {
  fallback = { ...(en as Table), ...extraStrings('en') };
  table = lang === 'en' ? fallback : { ...ios, ...extraStrings(lang) };
}
rebuild();
const loaders = import.meta.glob<Table>('./strings/*.json', { import: 'default' });

/** Загрузить словарь текущего языка (отдельный чанк). Вызывается до первого рендера. */
export async function loadStrings(): Promise<void> {
  if (lang === 'en') return;
  try { ios = await loaders[`./strings/${lang}.json`]!(); } catch { /* нет чанка — запасной en */ }
  rebuild();
}

const loading = new Map<StringArea, Promise<void>>();
/** Подключить строки области экрана W2–W3 (отдельный кусок, все языки файла). Повтор — тот же промис; не загрузилось —
 *  ошибка (экран не рисуется ключами), следующий вызов пробует снова. */
export function loadArea(area: StringArea): Promise<void> {
  if (extras[area]) return Promise.resolve();
  const load = areaStrings[area];
  if (!load) return Promise.reject(new Error(`нет строк области ${area}`));
  let pending = loading.get(area);
  if (!pending) {
    pending = load().then((file) => { extras[area] = file; rebuild(); }).finally(() => loading.delete(area));
    loading.set(area, pending);
  }
  return pending;
}

/** Строка интерфейса; {0}, {1}… подставляются по порядку. */
export function t(key: StringKey, ...args: Array<string | number>): string {
  const template = table[key] ?? fallback[key] ?? key;
  return template.replace(/\{(\d+)\}/g, (_, i: string) => String(args[Number(i)] ?? ''));
}

// Даты: всегда григорианский календарь и латинские цифры (урок iOS: системный календарь бывает буддийским).
export const dateLocale = `${lang}-u-ca-gregory-nu-latn`;

export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(dateLocale, options).format(date);
}
